import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'

/**
 * Every geometry the catalog uses goes through this cache, keyed by its exact
 * parameters. Two hundred identical desk legs across a floor plan therefore
 * share a single BufferGeometry — and, downstream, a single cached
 * EdgesGeometry for the contour pass.
 *
 * All factories return geometry that is CENTRED ON THE ORIGIN and Y-up, so an
 * asset positions parts by their centre. The only exception is `slabGeo`-family
 * output, which is also centred (use `position={[0, h / 2, 0]}` to sit on the floor).
 */
const cache = new Map<string, THREE.BufferGeometry>()

function memo<T extends THREE.BufferGeometry>(key: string, create: () => T): T {
  const hit = cache.get(key)
  if (hit) return hit as T
  const geometry = create()
  geometry.name = key
  cache.set(key, geometry)
  return geometry
}

const r2 = (n: number) => Math.round(n * 1e4) / 1e4

/* ------------------------------------------------------------------ boxes */

export function boxGeo(w: number, h: number, d: number): THREE.BoxGeometry {
  return memo(`box:${r2(w)},${r2(h)},${r2(d)}`, () => new THREE.BoxGeometry(w, h, d))
}

/** Box with a bevel on every edge. Keep `radius` small (<= 0.03) or contours break up. */
export function roundedBoxGeo(
  w: number,
  h: number,
  d: number,
  radius = 0.012,
  segments = 2,
): THREE.BufferGeometry {
  return memo(`rbox:${r2(w)},${r2(h)},${r2(d)},${r2(radius)},${segments}`, () => {
    const safe = Math.min(radius, w / 2 - 1e-4, h / 2 - 1e-4, d / 2 - 1e-4)
    return new RoundedBoxGeometry(w, h, d, segments, Math.max(safe, 1e-4))
  })
}

/* ---------------------------------------------------------------- profiles */

/** Rounded rectangle in the XY plane, centred. */
export function roundedRectShape(w: number, d: number, radius: number): THREE.Shape {
  const r = Math.max(0, Math.min(radius, w / 2, d / 2))
  const x = w / 2
  const y = d / 2
  const shape = new THREE.Shape()
  shape.moveTo(-x + r, -y)
  shape.lineTo(x - r, -y)
  shape.quadraticCurveTo(x, -y, x, -y + r)
  shape.lineTo(x, y - r)
  shape.quadraticCurveTo(x, y, x - r, y)
  shape.lineTo(-x + r, y)
  shape.quadraticCurveTo(-x, y, -x, y - r)
  shape.lineTo(-x, -y + r)
  shape.quadraticCurveTo(-x, -y, -x + r, -y)
  return shape
}

/** Racetrack / stadium profile — the classic boardroom table plan. */
export function stadiumShape(w: number, d: number): THREE.Shape {
  return roundedRectShape(w, d, Math.min(w, d) / 2)
}

export function ellipseShape(w: number, d: number): THREE.Shape {
  const shape = new THREE.Shape()
  shape.absellipse(0, 0, w / 2, d / 2, 0, Math.PI * 2, false, 0)
  return shape
}

/**
 * Extrudes a 2D plan shape upwards into a Y-up slab centred on the origin.
 * Used for floor platforms, desktops with rounded plans, table tops, seat pads.
 */
export function extrudeShapeGeo(
  shape: THREE.Shape,
  height: number,
  key: string,
  curveSegments = 10,
): THREE.BufferGeometry {
  return memo(`extrude:${key}:${r2(height)}:${curveSegments}`, () => {
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: height,
      bevelEnabled: false,
      curveSegments,
    })
    // Extrusion runs 0 -> +height along Z; rotating maps that onto 0 -> +height
    // along Y, so the solid has to come DOWN by half a thickness to end up
    // centred on the origin like every other factory here.
    geometry.rotateX(-Math.PI / 2)
    geometry.translate(0, -height / 2, 0)
    geometry.computeVertexNormals()
    return geometry
  })
}

/** Plan-rounded slab: sharp top/bottom faces, rounded corners seen from above. */
export function slabGeo(
  w: number,
  d: number,
  h: number,
  radius = 0.02,
  curveSegments = 8,
): THREE.BufferGeometry {
  const key = `slab:${r2(w)},${r2(d)},${r2(radius)}`
  return extrudeShapeGeo(roundedRectShape(w, d, radius), h, key, curveSegments)
}

export function stadiumSlabGeo(w: number, d: number, h: number): THREE.BufferGeometry {
  return extrudeShapeGeo(stadiumShape(w, d), h, `stadium:${r2(w)},${r2(d)}`, 14)
}

export function ellipseSlabGeo(w: number, d: number, h: number): THREE.BufferGeometry {
  return extrudeShapeGeo(ellipseShape(w, d), h, `ellipse:${r2(w)},${r2(d)}`, 14)
}

/* ------------------------------------------------------------ revolutions */

export function cylinderGeo(
  radiusTop: number,
  radiusBottom: number,
  height: number,
  radialSegments = 24,
  openEnded = false,
): THREE.CylinderGeometry {
  return memo(
    `cyl:${r2(radiusTop)},${r2(radiusBottom)},${r2(height)},${radialSegments},${openEnded}`,
    () => new THREE.CylinderGeometry(radiusTop, radiusBottom, height, radialSegments, 1, openEnded),
  )
}

export function tubeGeo(radius: number, height: number, radialSegments = 16): THREE.CylinderGeometry {
  return cylinderGeo(radius, radius, height, radialSegments)
}

export function sphereGeo(radius: number, widthSegments = 20, heightSegments = 14): THREE.SphereGeometry {
  return memo(
    `sphere:${r2(radius)},${widthSegments},${heightSegments}`,
    () => new THREE.SphereGeometry(radius, widthSegments, heightSegments),
  )
}

export function capsuleGeo(radius: number, length: number, capSegments = 6, radialSegments = 16) {
  return memo(
    `capsule:${r2(radius)},${r2(length)},${capSegments},${radialSegments}`,
    () => new THREE.CapsuleGeometry(radius, length, capSegments, radialSegments),
  )
}

export function torusGeo(radius: number, tube: number, radialSegments = 10, tubularSegments = 32) {
  return memo(
    `torus:${r2(radius)},${r2(tube)},${radialSegments},${tubularSegments}`,
    () => new THREE.TorusGeometry(radius, tube, radialSegments, tubularSegments),
  )
}

/**
 * Lathe from a `[radius, y]` profile — pots, vases, bins, table pedestals.
 * The profile is given bottom-to-top in metres.
 */
export function latheGeo(
  profile: readonly (readonly [number, number])[],
  segments = 28,
  key?: string,
): THREE.LatheGeometry {
  const id = key ?? profile.map(([x, y]) => `${r2(x)}_${r2(y)}`).join('|')
  return memo(`lathe:${id}:${segments}`, () => {
    const points = profile.map(([x, y]) => new THREE.Vector2(Math.max(x, 1e-5), y))
    return new THREE.LatheGeometry(points, segments)
  })
}

/* ---------------------------------------------------------------- 2D forms */

/**
 * A leaf / petal blade: a flat, slightly tapered lens shape used by the plants.
 * Lives in the XY plane, centred at its stem end (origin at the bottom tip).
 */
export function leafGeo(length: number, width: number, curveSegments = 12): THREE.BufferGeometry {
  return memo(`leaf:${r2(length)},${r2(width)}`, () => {
    const shape = new THREE.Shape()
    shape.moveTo(0, 0)
    shape.bezierCurveTo(width * 0.9, length * 0.25, width * 0.55, length * 0.8, 0, length)
    shape.bezierCurveTo(-width * 0.55, length * 0.8, -width * 0.9, length * 0.25, 0, 0)
    const geometry = new THREE.ShapeGeometry(shape, curveSegments)
    geometry.computeVertexNormals()
    return geometry
  })
}

/** Thin flat panel (a box, so it still produces a clean four-edge contour). */
export function panelGeo(w: number, h: number, thickness = 0.012): THREE.BoxGeometry {
  return boxGeo(w, h, thickness)
}

/* Contour extraction lives in `./contour.ts` — it needs face adjacency, not just creases. */
